/* eslint-disable no-param-reassign */
import { ElementName, FabricObject } from '@meitu/whee-infinite-canvas';
import {
  StateCreator, UseBoundStore, StoreApi, create,
} from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';
// eslint-disable-next-line import/no-cycle
import useCanvasStore from '$/store/useCanvasStore';
// eslint-disable-next-line import/no-cycle
import { saveLayerData } from '$/pages/RoboneoCanvas/components/InfiniteCanvas/layerData';
import { ColorState, formatColor, formatColorToRab } from '$/utils/colorUtil';
import useCanvasObjectStore from './useCanvasObjectStore';
import type { Write, WithImmer } from './immer.d';
import type { StorePersist } from './persist.d';

interface ActiveGraphicState {
  width: string;
  height: string;
  fill: ColorState;
  stroke: ColorState;
  strokeWidth: string;
  rx?: string;
  isLock: boolean;
}
interface GraphicEditState {
  activeGraphicStatus: ActiveGraphicState | null;
  strokeLastColorData: LastColorData;
  fillLastColorData: LastColorData;
  lastModifiedDimension: 'width' | 'height' | null;
}

interface LastColorData {
  [uid: string]: ColorState[];
}
const graphicEditSlice: StateCreator<GraphicEditState, [
  ['zustand/immer', never],
]> = () => ({
  fillLastColorData: {} as LastColorData,
  strokeLastColorData: {} as LastColorData,
  activeGraphicStatus: null,
  lastModifiedDimension: null,
});
const useGraphicToolStore: UseBoundStore<Write<WithImmer<StoreApi<GraphicEditState>>, StorePersist<GraphicEditState, {
  fillLastColorData: LastColorData;
  strokeLastColorData: LastColorData;
  lastModifiedDimension: 'width' | 'height' | null;
}>>> = create<GraphicEditState>()(immer(persist(graphicEditSlice, {
  name: 'useGraphicToolStore',
  version: 1,
  partialize: (state) => ({
    fillLastColorData: state.fillLastColorData,
    strokeLastColorData: state.strokeLastColorData,
    lastModifiedDimension: state.lastModifiedDimension,
  }),
})));
export default useGraphicToolStore;

function initObjectScale() {
  const currentObject = useCanvasObjectStore.getState().currentObjects[0];
  if (!currentObject) return;
  currentObject.set({ scaleX: 1, scaleY: 1 });
  const { render } = useCanvasStore.getState();
  render?._FC.renderAll();
}

/**
 * 设置填充最近颜色
 *
 * @export
 * @param {ColorState} color
 * @return {*}
 */
export function setFillLastColorData(color: ColorState) {
  // eslint-disable-next-line ban/ban
  const uid = localStorage.getItem('uid');
  if (!uid || !color.rgb.a) return;
  useGraphicToolStore.setState((state) => {
    const colorList = state.fillLastColorData![uid];
    if (colorList) {
      const has = colorList.some((item) => {
        return JSON.stringify(item.rgb) === JSON.stringify(color.rgb);
      });
      // 如果已有相同颜色，则不缓存
      if (has) return;
      if (colorList.length >= 6) {
        const newList = [...state.fillLastColorData![uid]];
        newList.unshift(color);
        newList.pop();
        state.fillLastColorData![uid] = newList;
      } else {
        state.fillLastColorData![uid].unshift(color);
      }
    } else {
      state.fillLastColorData![uid] = [color];
    }
  });
}

/**
 * 设置描边最近颜色
 *
 * @export
 * @param {ColorState} color
 * @return {*}
 */
export function setStrokeLastColorData(color: ColorState) {
  // eslint-disable-next-line ban/ban
  const uid = localStorage.getItem('uid');
  if (!uid || !color.rgb.a) return;
  useGraphicToolStore.setState((state) => {
    const colorList = state.strokeLastColorData![uid];
    if (colorList) {
      const has = colorList.some((item) => {
        return JSON.stringify(item.rgb) === JSON.stringify(color.rgb);
      });
      // 如果已有相同颜色，则不缓存
      if (has) return;
      if (colorList.length >= 8) {
        const newList = [...state.strokeLastColorData![uid]];
        newList.unshift(color);
        newList.pop();
        state.strokeLastColorData![uid] = newList;
      } else {
        state.strokeLastColorData![uid].unshift(color);
      }
    } else {
      state.strokeLastColorData![uid] = [color];
    }
  });
}

/**
 * 设置图形工具所有字段
 *
 * @export
 * @param {(ActiveTextStatus | null)} status
 */
export function setActiveGraphicStatus(status: ActiveGraphicState | null) {
  useGraphicToolStore.setState(() => ({
    activeGraphicStatus: status,
  }));
}

/**
 * 更新图形工具字段
 *
 * @export
 * @param {keyof ActiveTextStatus} key
 * @param {*} value
 */
export function updateActiveGraphicStatus(key: keyof ActiveGraphicState, value: string | boolean | ColorState) {
  useGraphicToolStore.setState((state) => {
    if (state.activeGraphicStatus) {
      state.activeGraphicStatus = {
        ...state.activeGraphicStatus,
        [key]: value,
      };
    }
  });
}

/**
 * 宽度设置
 *
 * @param {number} width
 * @return {*}
 */

export function setWidth(width: number) {
  updateActiveGraphicStatus('width', `${width}`);
  // 更新最后修改的维度为宽度
  useGraphicToolStore.setState({ lastModifiedDimension: 'width' });
  const currentObject = useCanvasObjectStore.getState().currentObjects[0];
  initObjectScale();
  const { render } = useCanvasStore.getState();
  if (currentObject._name_ === ElementName.ELLIPSE) {
    currentObject.set({ rx: width / 2 });
  } else {
    currentObject.set({ width });
  }
  currentObject.setCoords();
  render?._FC.renderAll();
  saveLayerData(null, [currentObject]);
}

/**
 * 高度设置
 *
 * @param {number} height
 * @return {*}
 */

export function setHeight(height: number) {
  updateActiveGraphicStatus('height', `${height}`);
  // 更新最后修改的维度为高度
  useGraphicToolStore.setState({ lastModifiedDimension: 'height' });
  const currentObject = useCanvasObjectStore.getState().currentObjects[0];
  initObjectScale();
  const { render } = useCanvasStore.getState();
  if (currentObject._name_ === ElementName.ELLIPSE) {
    currentObject.set({ ry: height / 2 });
  } else {
    currentObject.set({ height });
  }
  currentObject.setCoords();
  render?._FC.renderAll();
  saveLayerData(null, [currentObject]);
}

/**
 * 设置填充颜色
 *
 * @param {ColorState} color
 * @return {*}
 */
export function setFillColor(color: ColorState) {
  updateActiveGraphicStatus('fill', color);
  const colorStr = formatColorToRab(color);
  const currentObject = useCanvasObjectStore.getState().currentObjects[0];
  // submitModifyHistory(() => {
  const { render } = useCanvasStore.getState();
  currentObject?.set({ fill: colorStr });
  render?._FC.renderAll();
  saveLayerData(null, [currentObject]);
  // });
}

/**
 * 设置描边颜色
 *
 * @param {string} color
 * @return {*}
 */

export function setStrokeColor(color: ColorState) {
  updateActiveGraphicStatus('stroke', color);
  const colorStr = formatColorToRab(color);
  const currentObject = useCanvasObjectStore.getState().currentObjects[0];
  // submitModifyHistory(() => {
  const { render } = useCanvasStore.getState();
  currentObject?.set({ stroke: colorStr });
  if (currentObject.strokeWidth === 0) {
    updateActiveGraphicStatus('strokeWidth', '5');
    currentObject?.set({ strokeWidth: 5 });
  }
  render?._FC.renderAll();
  saveLayerData(null, [currentObject]);
  // });
}

/**
 * 设置描边宽度
 *
 * @param {number} width
 * @return {*}
 */

export function setStrokeWidth(width: number) {
  updateActiveGraphicStatus('strokeWidth', `${width}`);
  const currentObject = useCanvasObjectStore.getState().currentObjects[0];
  const { render } = useCanvasStore.getState();
  const options = {
    strokeWidth: width,
  };
  if (currentObject._name_ === ElementName.ARROW) {
    initObjectScale();
  }
  currentObject.set(options);
  currentObject.setCoords();
  render?._FC.renderAll();
  saveLayerData(null, [currentObject]);
}

/**
 * 设置圆角描边宽度
 *
 * @param {number} width
 * @return {*}
 */

export function setRadius(width: number) {
  updateActiveGraphicStatus('rx', `${width}`);
  const currentObject = useCanvasObjectStore.getState().currentObjects[0];
  const { render } = useCanvasStore.getState();
  initObjectScale();
  currentObject.set({ rx: width, ry: width });
  render?._FC.renderAll();
  saveLayerData(null, [currentObject]);
}

// 初始化选中的图层的数据
export function initGraphicStatus(fabricObject: FabricObject): ActiveGraphicState {
  const fillColor = formatColor((fabricObject.fill || 'rgba(0, 0, 0, 0)') as string);
  const strokeColor = formatColor((fabricObject.stroke || 'rgba(255, 255, 255, 1)') as string);
  const rawWidth = fabricObject.getScaledWidth();
  const rawHeight = fabricObject.getScaledHeight();
  const strokeWidth = fabricObject._name_ === ElementName.ARROW ? fabricObject.strokeWidth * fabricObject.scaleX : fabricObject.strokeWidth;
  const graphicStatus = {
    width: `${Math.round(rawWidth)}`,
    height: `${Math.round(rawHeight)}`,
    fill: fillColor,
    stroke: strokeColor,
    strokeWidth: `${Math.round(strokeWidth)}`,
    rx: `${Math.round((fabricObject as any)?.rx || 0)}`,
    isLock: false,
  };
  setActiveGraphicStatus(graphicStatus);
  return graphicStatus;
}
